/**
 * Microsoft Graph API provider — replaces IMAP for Outlook.com/Office 365 accounts.
 * Uses Graph API for reliable, fast mail sync with native write-back.
 *
 * SOURCE OF TRUTH: this file is the canonical Outlook provider for mailx.
 * Same Provider abstraction as Gmail and IMAP, lives alongside them so future
 * Android Outlook support uses the same code with no re-implementation.
 *
 * Modeled on gmail.ts (the gold-standard provider): shared module-level token
 * bucket, robust 429/5xx retry with Retry-After + shared cooldown + terminal
 * cooldown, and provider_id-first identity for every per-message op. Graph's
 * string IDs ARE identity; the integer uid is a sort/display convenience only.
 * See the imap_uid_not_identity lesson — any per-message op keyed solely by the
 * hashed uid must list-and-hash, which is capped and breaks on large folders,
 * so we always prefer the caller-supplied Graph id.
 *
 * Platform requirements: globalThis.fetch (Node 18+ and all browsers/WebViews),
 * atob/btoa, Uint8Array, TextDecoder, TextEncoder. No Node-specific imports
 * (no node:*, no Buffer) — this runs in the Android WebView too.
 */

import type { MailProvider, ProviderFolder, ProviderMessage, FetchOptions } from "./types.js";

const GRAPH = "https://graph.microsoft.com/v1.0/me";

/** Map Graph well-known folder names to specialUse */
const WELL_KNOWN: Record<string, string> = {
    inbox: "inbox",
    sentitems: "sent",
    drafts: "drafts",
    deleteditems: "trash",
    junkemail: "junk",
    archive: "archive",
};

/** Rate-limit + cooldown state shared across all OutlookApiProvider instances.
 *  Graph throttles per mailbox/app, not per JS object, so sharing state here
 *  prevents a fresh `new OutlookApiProvider(...)` from side-stepping the
 *  cooldown by creating a new instance. Single-account setups won't notice;
 *  multi-account setups are slightly over-throttled, which is safer than
 *  burning through Graph's tolerance. Mirrors gmail.ts's rateState exactly. */
const rateState = {
    tokens: 0,
    lastRefill: 0,
    cooldownUntil: 0,
    /** Tokens refilled per second (keeps us well under Graph's per-app caps). */
    rate: 8,
    /** Max burst above steady state. */
    burst: 8,
};
rateState.tokens = rateState.burst;
rateState.lastRefill = Date.now();

/** Base64-encode raw bytes without Node's Buffer. btoa wants a binary string
 *  (one char per byte); building it with String.fromCharCode(...wholeArray)
 *  blows the call-stack on large MIME, so we chunk. Used for /sendMail, where
 *  Graph expects the full RFC822 message as a base64 payload. */
function toBase64(bytes: Uint8Array): string {
    let binary = "";
    const CHUNK = 0x8000; // 32 KB per fromCharCode call — safely under arg limits
    for (let i = 0; i < bytes.length; i += CHUNK) {
        const slice = bytes.subarray(i, i + CHUNK);
        binary += String.fromCharCode.apply(null, slice as unknown as number[]);
    }
    return btoa(binary);
}

/** Graph well-known folder identifiers. These strings are valid folder ids in
 *  any /mailFolders/{id} URL or move destinationId — Graph accepts them
 *  interchangeably with the opaque GUID. resolveFolder lets them pass through
 *  untouched so callers (trash → "deleteditems") work even on a fresh instance
 *  whose hierarchical-path map hasn't been populated yet. */
const WELL_KNOWN_IDS = new Set<string>([
    "inbox", "drafts", "sentitems", "deleteditems", "junkemail", "archive",
]);

export class OutlookApiProvider implements MailProvider {
    constructor(private tokenProvider: () => Promise<string>) {}

    /** Hierarchical display path → Graph folder id, plus id → id (self) so a raw
     *  Graph id passed back in still resolves. Populated by listFolders. Instance-
     *  scoped (not module-shared like rateState) because folder ids are per-
     *  mailbox: two accounts share throttle state but never folder identity. */
    private folderIds = new Map<string, string>();

    /** Turn whatever the dispatcher hands us (a hierarchical display path like
     *  "Projects/2026/Invoices", a Graph well-known name, or a raw Graph id)
     *  into a Graph folder id usable in a /mailFolders/{id} URL or as a move
     *  destinationId. Disambiguation order:
     *    (a) Graph well-known name (inbox/drafts/…) → pass through; it IS an id.
     *    (b) Known hierarchical path or already-an-id in the map → mapped id.
     *    (c) Map empty (fresh instance — listFolders hasn't run this session,
     *        as happens for move/rename which never list first) → list once to
     *        populate, then re-check.
     *    (d) Still unknown → return as-is (back-compat: assume it's a raw Graph
     *        id we simply haven't catalogued, e.g. a destination outside the
     *        listed set). */
    private async resolveFolder(pathOrId: string): Promise<string> {
        if (WELL_KNOWN_IDS.has(pathOrId)) return pathOrId;
        const hit = this.folderIds.get(pathOrId);
        if (hit) return hit;
        if (this.folderIds.size === 0) {
            // Lazy populate: move/rename are reached without a prior listFolders
            // on a fresh provider instance. One list call fills the map.
            await this.listFolders();
            const after = this.folderIds.get(pathOrId);
            if (after) return after;
        }
        return pathOrId;
    }

    /** Block until (a) cooldown has elapsed and (b) a token is available.
     *  Token-bucket refill happens lazily on each call. Copy of gmail.ts's
     *  acquireToken so all providers share one throttling discipline. */
    private async acquireToken(): Promise<void> {
        // Respect an active cooldown first — refuse to even try until it ends.
        const now = Date.now();
        if (rateState.cooldownUntil > now) {
            const waitMs = rateState.cooldownUntil - now;
            console.log(`  [outlook] cooldown: waiting ${(waitMs / 1000).toFixed(1)}s before next request`);
            await new Promise(r => setTimeout(r, waitMs));
        }
        // Lazy refill, then spin-wait in chunks until a token is ready.
        while (true) {
            const t = Date.now();
            const elapsedSec = (t - rateState.lastRefill) / 1000;
            rateState.tokens = Math.min(rateState.burst, rateState.tokens + elapsedSec * rateState.rate);
            rateState.lastRefill = t;
            if (rateState.tokens >= 1) {
                rateState.tokens -= 1;
                return;
            }
            const waitMs = Math.max(20, ((1 - rateState.tokens) / rateState.rate) * 1000);
            await new Promise(r => setTimeout(r, waitMs));
        }
    }

    /** Compute a retry delay from the attempt number + an optional Retry-After
     *  header (seconds OR HTTP-date). Full jitter, capped. Shared by the JSON
     *  fetch and the raw fetch so both back off identically. Graph signals
     *  throttling via 429 + Retry-After — it has no 403-quota equivalent, so
     *  (unlike gmail.ts) there's no quota-403 branch. */
    private retryDelay(attempt: number, retryAfter: string | null): number {
        const baseDelayMs = 1000;
        const maxDelayMs = 60_000;
        let delay = baseDelayMs * Math.pow(2, attempt);
        if (retryAfter) {
            const asInt = parseInt(retryAfter, 10);
            if (!isNaN(asInt)) delay = asInt * 1000;
            else {
                const when = Date.parse(retryAfter);
                if (!isNaN(when)) delay = Math.max(0, when - Date.now());
            }
        }
        delay = Math.min(maxDelayMs, delay);
        // Full jitter to avoid synchronized retries across parallel workers.
        return Math.floor(delay * (0.5 + Math.random() * 0.5));
    }

    private async fetch(path: string, options: RequestInit = {}): Promise<any> {
        const token = await this.tokenProvider();
        const maxAttempts = 6;
        let lastStatus = 0;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await this.acquireToken();
            const res = await globalThis.fetch(`${GRAPH}${path}`, {
                ...options,
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                    ...options.headers,
                },
            });
            // Graph throttling = 429 with Retry-After. Treat 5xx the same way:
            // transient server-side, worth a backed-off retry.
            if (res.status === 429 || res.status >= 500) {
                lastStatus = res.status;
                const retryAfter = res.headers.get("Retry-After");
                const delay = this.retryDelay(attempt, retryAfter);
                // Install a shared cooldown so *other* in-flight workers wait
                // too, not just this one. Prevents 5 parallel workers each
                // retrying independently and keeping the mailbox throttled.
                rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + delay);
                console.log(`  [outlook] ${res.status} (attempt ${attempt + 1}/${maxAttempts}), waiting ${(delay / 1000).toFixed(1)}s${retryAfter ? ` (Retry-After: ${retryAfter})` : ""}...`);
                await new Promise(r => setTimeout(r, delay));
                continue;
            }
            if (!res.ok) {
                const err = await res.text().catch(() => "");
                throw new Error(`Graph API ${res.status}: ${err.substring(0, 200)}`);
            }
            // 204 No Content (PATCH/move/etc.) has no JSON body — don't choke.
            if (res.status === 204) return null;
            const text = await res.text();
            return text ? JSON.parse(text) : null;
        }
        // Exhausted all retries — Graph is clearly throttling hard. Park every
        // subsequent request behind a long cooldown so the next periodic sync
        // doesn't immediately fire another 6 retries into the same limit.
        // 5 minutes is a floor — a longer incoming Retry-After still wins via
        // the per-attempt cooldown set above.
        const TERMINAL_COOLDOWN_MS = 5 * 60_000;
        rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + TERMINAL_COOLDOWN_MS);
        console.log(`  [outlook] exhausted ${maxAttempts} retries — parking further requests for ${TERMINAL_COOLDOWN_MS / 1000}s`);
        throw new Error(`Graph API: failed after ${maxAttempts} retries (last status ${lastStatus})`);
    }

    /** Fetch raw bytes (for RFC 2822 $value endpoint). Goes through the same
     *  acquireToken + 429/5xx retry discipline as the JSON fetch — a body
     *  prefetch must not side-step the throttle and re-trigger a cooldown. */
    private async fetchRaw(path: string): Promise<string> {
        const token = await this.tokenProvider();
        const maxAttempts = 6;
        let lastStatus = 0;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await this.acquireToken();
            const res = await globalThis.fetch(`${GRAPH}${path}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            if (res.status === 429 || res.status >= 500) {
                lastStatus = res.status;
                const retryAfter = res.headers.get("Retry-After");
                const delay = this.retryDelay(attempt, retryAfter);
                rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + delay);
                console.log(`  [outlook raw] ${res.status} (attempt ${attempt + 1}/${maxAttempts}), waiting ${(delay / 1000).toFixed(1)}s${retryAfter ? ` (Retry-After: ${retryAfter})` : ""}...`);
                await new Promise(r => setTimeout(r, delay));
                continue;
            }
            if (!res.ok) {
                const err = await res.text().catch(() => "");
                throw new Error(`Graph API ${res.status} on raw fetch: ${err.substring(0, 200)}`);
            }
            return res.text();
        }
        const TERMINAL_COOLDOWN_MS = 5 * 60_000;
        rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + TERMINAL_COOLDOWN_MS);
        throw new Error(`Graph API raw: failed after ${maxAttempts} retries (last status ${lastStatus})`);
    }

    async listFolders(): Promise<ProviderFolder[]> {
        const folders: ProviderFolder[] = [];
        let url = "/mailFolders?$top=100";
        while (url) {
            const data = await this.fetch(url);
            for (const f of data.value || []) {
                const name: string = f.displayName || f.id;
                const specialUse = WELL_KNOWN[f.displayName?.toLowerCase()] || "";
                // Top-level path is just the display name. The client builds its
                // folder TREE by splitting `path` on the delimiter, so `path`
                // must be the HIERARCHICAL display path, not the flat Graph id —
                // otherwise the whole tree collapses to one level and same-leaf-
                // name folders under different parents look like duplicates.
                const path = name;
                folders.push({
                    path,
                    name,
                    delimiter: "/",
                    specialUse,
                    flags: [],
                });
                // Record both directions so resolveFolder can turn the path back
                // into a Graph id, and a raw id round-trips unchanged.
                this.folderIds.set(path, f.id);
                this.folderIds.set(f.id, f.id);
                // Fetch child folders — pass the hierarchical path (not the bare
                // display name) so deep paths nest correctly.
                if (f.childFolderCount > 0) {
                    const children = await this.fetchChildFolders(f.id, path);
                    folders.push(...children);
                }
            }
            url = data["@odata.nextLink"]?.replace(`${GRAPH}`, "") || "";
        }
        return folders;
    }

    /** Recurse into child folders. `parentPath` is the HIERARCHICAL display path
     *  of the parent (e.g. "Projects/2026"), so a child's path nests fully
     *  ("Projects/2026/Invoices") rather than flattening to one level. */
    private async fetchChildFolders(parentId: string, parentPath: string): Promise<ProviderFolder[]> {
        const folders: ProviderFolder[] = [];
        let url = `/mailFolders/${parentId}/childFolders?$top=100`;
        while (url) {
            const data = await this.fetch(url);
            for (const f of data.value || []) {
                const name: string = f.displayName || f.id;
                // Child path = parent's hierarchical path + this leaf, joined by
                // the delimiter the client splits on.
                const path = `${parentPath}/${name}`;
                folders.push({
                    path,
                    name,
                    delimiter: "/",
                    specialUse: "",
                    flags: [],
                });
                this.folderIds.set(path, f.id);
                this.folderIds.set(f.id, f.id);
                if (f.childFolderCount > 0) {
                    const children = await this.fetchChildFolders(f.id, path);
                    folders.push(...children);
                }
            }
            url = data["@odata.nextLink"]?.replace(`${GRAPH}`, "") || "";
        }
        return folders;
    }

    /** Convert Graph message to ProviderMessage */
    private parseMessage(msg: any, source = ""): ProviderMessage {
        const from = msg.from?.emailAddress
            ? [{ name: msg.from.emailAddress.name, address: msg.from.emailAddress.address }]
            : [];
        const to = (msg.toRecipients || []).map((r: any) => ({
            name: r.emailAddress?.name, address: r.emailAddress?.address,
        }));
        const cc = (msg.ccRecipients || []).map((r: any) => ({
            name: r.emailAddress?.name, address: r.emailAddress?.address,
        }));

        // Graph uses string IDs — hash to integer UID for sort/display.
        const uid = this.idToUid(msg.id);

        return {
            uid,
            messageId: msg.internetMessageId || "",
            providerId: msg.id,
            date: msg.receivedDateTime ? new Date(msg.receivedDateTime) : null,
            sentDate: msg.sentDateTime ? new Date(msg.sentDateTime) : undefined,
            subject: msg.subject || "",
            from, to, cc,
            // Threading headers aren't reliably returned on the list path
            // ($select=...,internetMessageHeaders is flaky / requires a per-item
            // expand). We leave them empty here; the full MIME pulled by
            // fetchOne/$value or fetchBodiesBatch carries In-Reply-To/References,
            // so threading is recovered downstream from the raw source.
            inReplyTo: "",
            references: [],
            seen: msg.isRead === true,
            flagged: msg.flag?.flagStatus === "flagged",
            answered: false,
            draft: msg.isDraft === true,
            size: 0, // Graph doesn't return size in list
            source,
        };
    }

    /** Stable integer UID from Graph string ID. NOT identity — only a
     *  sort/display convenience. Every per-message op prefers the Graph id. */
    private idToUid(id: string): number {
        // Graph IDs are long base64 strings — hash to a positive int.
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
        }
        return Math.abs(hash);
    }

    /** List messages in a folder with optional $filter. Tracks whether
     *  pagination was capped so getUids can flag truncation for reconcile. */
    private async listMessages(folderId: string, filter = "", top = 500, ordered = true): Promise<any[]> {
        // folderId may be a hierarchical display path (the client's `path`) — map
        // it back to the Graph id this URL needs. Resolving here covers every
        // read path: fetchSince/fetchByDate/fetchByUids/getUids/fetchBodiesBatch
        // and the fetchOne/resolveId list-and-hash fallbacks all route through.
        const id = await this.resolveFolder(folderId);
        const messages: any[] = [];
        const select = "id,subject,from,toRecipients,ccRecipients,receivedDateTime,isRead,isDraft,flag,internetMessageId,hasAttachments";
        // $orderby is only worth its cost when the caller needs newest-first
        // (the display/sync fetches). For id-only sweeps (getUids,
        // fetchBodiesBatch, resolveId) order is irrelevant, and Graph can throw
        // InefficientFilter when $orderby is combined with deep pagination of a
        // large unfiltered folder — so those callers pass ordered=false.
        let url = `/mailFolders/${id}/messages?$select=${select}&$top=${Math.min(top, 100)}`;
        if (ordered) url += `&$orderby=receivedDateTime desc`;
        if (filter) url += `&$filter=${encodeURIComponent(filter)}`;
        let truncated = false;
        while (url) {
            const data = await this.fetch(url);
            messages.push(...(data.value || []));
            const next = data["@odata.nextLink"]?.replace(`${GRAPH}`, "") || "";
            if (!next) break;
            if (messages.length >= top) {
                // Hit the caller's cap but the server has more pages. Flag it
                // so reconcile-style callers refuse to treat this as complete.
                truncated = true;
                break;
            }
            url = next;
        }
        (messages as any)._truncated = truncated;
        return messages;
    }

    async fetchSince(folder: string, sinceUid: number, options: FetchOptions = {}): Promise<ProviderMessage[]> {
        // The hashed uid is NOT monotonic — filtering `m.uid > sinceUid`
        // silently drops new mail whose hash falls below the high-water mark
        // (same trap gmail.ts documents). When the caller bounds by date, use
        // a receivedDateTime filter; otherwise just return the recent page and
        // let the caller reconcile by Graph id.
        void sinceUid;
        const filter = options.since ? `receivedDateTime ge ${options.since.toISOString()}` : "";
        const cap = options.since ? 0 : 200;
        // cap 0 → effectively unbounded (date filter is self-limiting); use a
        // high ceiling so listMessages still terminates.
        const msgs = await this.listMessages(folder, filter, cap === 0 ? 10000 : cap);
        let results = msgs.map(m => this.parseMessage(m));
        if (options.source) {
            results = await this.addSources(results);
        }
        return results;
    }

    async fetchByDate(folder: string, since: Date, before: Date, options: FetchOptions = {},
        onChunk?: (msgs: ProviderMessage[]) => void): Promise<ProviderMessage[]> {
        const filter = `receivedDateTime ge ${since.toISOString()} and receivedDateTime lt ${before.toISOString()}`;
        // Date-bounded — let pagination drain the full range (high ceiling).
        const msgs = await this.listMessages(folder, filter, 10000);
        const all: ProviderMessage[] = [];
        const chunkSize = 25;
        for (let i = 0; i < msgs.length; i += chunkSize) {
            const chunk = msgs.slice(i, i + chunkSize);
            let parsed = chunk.map(m => this.parseMessage(m));
            if (options.source) {
                parsed = await this.addSources(parsed);
            }
            all.push(...parsed);
            if (onChunk) onChunk(parsed);
        }
        return all;
    }

    async fetchByUids(folder: string, uids: number[], options: FetchOptions = {}): Promise<ProviderMessage[]> {
        const uidSet = new Set(uids);
        const msgs = await this.listMessages(folder, "", 10000, false);
        let results = msgs.map(m => this.parseMessage(m)).filter(m => uidSet.has(m.uid));
        if (options.source) {
            results = await this.addSources(results);
        }
        return results;
    }

    async fetchOne(folder: string, uid: number, options: FetchOptions = {}): Promise<ProviderMessage | null> {
        // Caller passes providerId straight from the DB row when available —
        // skips the list-the-whole-folder round trip entirely (mirrors
        // gmail.ts). With the Graph id we GET the message directly, and pull
        // the full MIME via /$value only when source is requested.
        const id = options.providerId;
        if (id) {
            const select = "id,subject,from,toRecipients,ccRecipients,receivedDateTime,isRead,isDraft,flag,internetMessageId,hasAttachments";
            const msg = await this.fetch(`/messages/${id}?$select=${select}`);
            if (!msg) return null;
            let source = "";
            if (options.source) {
                source = await this.fetchRaw(`/messages/${id}/$value`);
            }
            return this.parseMessage(msg, source);
        }
        // Fallback: no providerId — list-and-hash, capped. Warn because this is
        // the slow/lossy path (a message past the cap won't be found).
        console.warn(`  [outlook] fetchOne UID ${uid} in ${folder}: no providerId, falling back to capped list-and-hash`);
        const msgs = await this.listMessages(folder, "", 1000, false);
        const found = msgs.find(m => this.idToUid(m.id) === uid);
        if (!found) return null;
        let source = "";
        if (options.source) {
            source = await this.fetchRaw(`/messages/${found.id}/$value`);
        }
        return this.parseMessage(found, source);
    }

    /** Bulk-fetch raw bodies for many UIDs in one folder. Lists the folder
     *  once to build a uid→Graph-id map, then pulls each wanted message's MIME
     *  via /$value with bounded concurrency (4 workers). We deliberately do NOT
     *  use Graph $batch multipart here: bounded-concurrency individual GETs are
     *  simpler and Graph's per-request reliability doesn't need the fragile
     *  multipart parsing. Mirrors gmail.ts's fetchBodiesIndividually shape. */
    async fetchBodiesBatch(
        folder: string,
        uids: number[],
        onBody: (uid: number, source: string) => void,
    ): Promise<void> {
        if (uids.length === 0) return;
        const msgs = await this.listMessages(folder, "", 10000, false);
        const uidToId = new Map<number, string>();
        for (const m of msgs) uidToId.set(this.idToUid(m.id), m.id);
        const wanted: { uid: number; id: string }[] = [];
        for (const uid of uids) {
            const id = uidToId.get(uid);
            if (id) wanted.push({ uid, id }); // skip uids that don't resolve
        }
        if (wanted.length === 0) return;

        const CONCURRENCY = 4;
        let cursor = 0;
        const worker = async (): Promise<void> => {
            while (cursor < wanted.length) {
                const idx = cursor++;
                const { uid, id } = wanted[idx];
                try {
                    const source = await this.fetchRaw(`/messages/${id}/$value`);
                    onBody(uid, source);
                } catch (e: any) {
                    // Per-message error must not abort the whole batch.
                    console.error(`  [outlook prefetch] UID ${uid}: ${e.message}`);
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, wanted.length) }, () => worker()));
    }

    /** Apply the absolute flag state to a message. Graph model: isRead boolean
     *  + flag.flagStatus. We send both so the end state matches regardless of
     *  prior state (idempotent, safe to retry). */
    async setFlags(folder: string, uid: number, flags: string[], providerId?: string): Promise<void> {
        // Prefer the caller-supplied Graph id (stored provider_id). Without it
        // we fall back to a capped list-and-hash, which misses any message past
        // the cap — so flagging an older message would throw, the flag would
        // never reach Graph, and the local star would revert on next sync.
        // Same rationale as gmail.ts's setFlags.
        const id = await this.resolveId(folder, uid, providerId, "setFlags");
        const flagSet = new Set(flags);
        await this.fetch(`/messages/${id}`, {
            method: "PATCH",
            body: JSON.stringify({
                isRead: flagSet.has("\\Seen"),
                flag: { flagStatus: flagSet.has("\\Flagged") ? "flagged" : "notFlagged" },
            }),
        });
    }

    /** Move a message to Deleted Items. Graph's well-known folder name
     *  "deleteditems" is accepted as a move destination. */
    async trashMessage(folder: string, uid: number, providerId?: string): Promise<void> {
        const id = await this.resolveId(folder, uid, providerId, "trashMessage");
        await this.moveById(id, "deleteditems");
    }

    /** Move a message to another folder. `toFolder` is the client's `path` (a
     *  hierarchical display path) — resolve it to the Graph destination id. The
     *  source folder is only needed for the resolveId list-and-hash fallback,
     *  which resolveFolder handles internally via listMessages. */
    async moveMessage(fromFolder: string, uid: number, toFolder: string, providerId?: string): Promise<void> {
        const id = await this.resolveId(fromFolder, uid, providerId, "moveMessage");
        const dest = await this.resolveFolder(toFolder);
        await this.moveById(id, dest);
    }

    /** POST /messages/{id}/move, treating 404 as already-done.
     *
     *  CRITICAL (differs from Gmail): a Graph move returns a NEW message id in
     *  the destination — the source id we hold becomes invalid. So if a move
     *  succeeds server-side but the ACK is lost, the queued retry re-fires with
     *  the now-stale id and Graph answers 404. Throwing there would exhaust the
     *  sync-action retries and `clearTombstoneForUid` would RESURRECT a message
     *  that was actually moved (the exact "deletions un-happening" class the
     *  Gmail provider_id fix killed). A 404 on a move means "it's no longer
     *  here" — which is the goal — so we swallow it as success. */
    private async moveById(id: string, destinationId: string): Promise<void> {
        try {
            await this.fetch(`/messages/${id}/move`, {
                method: "POST",
                body: JSON.stringify({ destinationId }),
            });
        } catch (e: any) {
            if (/\b404\b/.test(e?.message || "")) return; // already moved / gone
            throw e;
        }
    }

    /** Rename and/or reparent a mail folder. `folderPath` and `newParentPath`
     *  are the client's hierarchical display paths — resolve each to its Graph
     *  id before use.
     *
     *  - Rename: PATCH /mailFolders/{id} { displayName }.
     *  - Reparent: POST /mailFolders/{id}/move { destinationId }.
     *  Both can apply in one call: move first (so the folder lands under the new
     *  parent), then patch the display name.
     *
     *  Well-known folders (inbox/sentitems/drafts/deleteditems/junkemail/archive)
     *  must not be renamed — Graph rejects it, and it would corrupt the special-
     *  use mapping. We guard on both the well-known name (the path may be the bare
     *  name) and the folder's leaf display name, lower-cased. */
    async renameFolder(folderPath: string, newName: string, newParentPath?: string): Promise<void> {
        // Guard against renaming a special-use folder. The path's leaf segment is
        // the display name Graph would also expose as a well-known name.
        const leaf = (folderPath.split("/").pop() || folderPath).toLowerCase();
        if (WELL_KNOWN[leaf] || WELL_KNOWN_IDS.has(folderPath.toLowerCase())) {
            throw new Error(`"${newName}" maps to a well-known Outlook folder and can't be renamed.`);
        }
        const id = await this.resolveFolder(folderPath);
        if (newParentPath && newParentPath.length) {
            const dest = await this.resolveFolder(newParentPath);
            await this.fetch(`/mailFolders/${id}/move`, {
                method: "POST",
                body: JSON.stringify({ destinationId: dest }),
            });
        }
        await this.fetch(`/mailFolders/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ displayName: newName }),
        });
    }

    /** Send a full RFC822 MIME message via Graph's /sendMail. Graph accepts a
     *  base64-encoded MIME payload when the request Content-Type is text/plain
     *  and the body is the base64 string. We don't route this through the JSON
     *  fetch() helper (different content type) but still acquire a token and
     *  apply a simple inline retry. */
    async sendRaw(mime: string): Promise<void> {
        // Encode the MIME as UTF-8 bytes, then base64 (Buffer-free). btoa on a
        // unicode string would mangle multi-byte chars, so go via TextEncoder.
        const bytes = new TextEncoder().encode(mime);
        const payload = toBase64(bytes);
        const maxAttempts = 6;
        let lastStatus = 0;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await this.acquireToken();
            const token = await this.tokenProvider();
            const res = await globalThis.fetch(`${GRAPH}/sendMail`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "text/plain",
                },
                body: payload,
            });
            if (res.status === 429 || res.status >= 500) {
                lastStatus = res.status;
                const retryAfter = res.headers.get("Retry-After");
                const delay = this.retryDelay(attempt, retryAfter);
                rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + delay);
                console.log(`  [outlook sendMail] ${res.status} (attempt ${attempt + 1}/${maxAttempts}), waiting ${(delay / 1000).toFixed(1)}s...`);
                await new Promise(r => setTimeout(r, delay));
                continue;
            }
            // /sendMail returns 202 Accepted with an empty body on success.
            if (!res.ok) {
                const err = await res.text().catch(() => "");
                throw new Error(`Graph sendMail ${res.status}: ${err.substring(0, 200)}`);
            }
            return;
        }
        throw new Error(`Graph sendMail: failed after ${maxAttempts} retries (last status ${lastStatus})`);
    }

    /** Resolve a Graph message id: prefer the caller-supplied provider_id;
     *  otherwise list-and-hash (capped) and throw if not found. Centralizes the
     *  rationale shared by setFlags/trash/move — the cap means a write to a
     *  message past the most-recent ~1000 fails loudly rather than silently
     *  hitting the wrong message. */
    private async resolveId(folder: string, uid: number, providerId: string | undefined, op: string): Promise<string> {
        if (providerId) return providerId;
        const msgs = await this.listMessages(folder, "", 1000, false);
        const found = msgs.find(m => this.idToUid(m.id) === uid);
        if (!found) throw new Error(`Graph ${op}: UID ${uid} not found in ${folder} (no providerId, past list cap)`);
        return found.id;
    }

    async getUids(folder: string): Promise<number[]> {
        const msgs = await this.listMessages(folder, "", 10000, false);
        const result = msgs.map(m => this.idToUid(m.id));
        // Propagate the truncation flag so reconcile refuses to delete when the
        // server list was capped (mirrors gmail.ts) — otherwise a truncated
        // page would look like "these are all the messages" and the rest would
        // be deleted from the local DB.
        if ((msgs as any)._truncated) (result as any)._truncated = true;
        return result;
    }

    async close(): Promise<void> {
        // No persistent connection to close.
    }

    /** Add RFC 2822 source to messages by pulling each /$value. Sequential —
     *  bodies-with-source paths are already date- or uid-bounded so the count
     *  is small; bulk prefetch uses fetchBodiesBatch's worker pool instead. */
    private async addSources(messages: ProviderMessage[]): Promise<ProviderMessage[]> {
        for (const msg of messages) {
            try {
                msg.source = await this.fetchRaw(`/messages/${msg.providerId}/$value`);
            } catch { /* source unavailable — leave empty */ }
        }
        return messages;
    }
}
